import { BadgeColor } from '@/components/ui/catalyst/badge';
import {
  TreatmentRecordRequestSchema,
  TreatmentRecordTypes,
} from '@/services/api/generated/treatmentRecordRequestsService';

export type ChannelType = 'fax' | 'email' | 'mail' | 'phone' | null;

/**
 * Color mapping for treatment record types/scopes.
 */
export const SCOPE_COLORS: Record<TreatmentRecordTypes, BadgeColor> = {
  Bills: 'green',
  Records: 'blue',
  'Facility Records': 'blue',
  'Facility Bills': 'green',
  Radiology: 'purple',
  'ER Physician Bills': 'orange',
  Other: 'zinc',
  Unknown: 'zinc',
};

export const inferChannelType = (
  pointType: string | undefined,
): ChannelType => {
  if (!pointType) return null;
  const upperType = pointType.toUpperCase();
  switch (upperType) {
    case 'FAX':
      return 'fax';
    case 'EMAIL':
      return 'email';
    case 'MAILING_ADDRESS':
      return 'mail';
    case 'PHONE':
      return 'phone';
    default:
      return null;
  }
};

/**
 * Determines whether a request's metadata indicates it was created from a
 * hospital-style template (ER or radiology groups).
 */
const isHospitalTemplateKey = (
  groupKey: string,
  providedRecords?: string[],
): boolean => {
  const lowerKey = groupKey.toLowerCase();
  return (
    lowerKey.includes('radiology') ||
    lowerKey.includes('er_') ||
    lowerKey.includes('er-') ||
    lowerKey === 'er' ||
    lowerKey.startsWith('er_') ||
    (providedRecords ?? []).some(
      (scope) => scope === 'Radiology' || scope.startsWith('ER '),
    )
  );
};

export interface RequestOriginInfo {
  label: string;
  color: BadgeColor;
  /** When set, render as a compact icon with tooltip instead of a text badge */
  tooltip?: string;
}

/**
 * Inspects request metadata to determine if the request was created from a
 * provider request plan or an auto-template, and returns display info.
 */
export const getRequestOrigin = (
  request: TreatmentRecordRequestSchema,
): RequestOriginInfo | null => {
  const metadata = request.metadata as
    | {
        prp_plan_id?: unknown;
        prp_request_group_key?: unknown;
      }
    | undefined;

  const prpPlanId =
    typeof metadata?.prp_plan_id === 'string' && metadata.prp_plan_id
      ? metadata.prp_plan_id
      : null;
  const prpGroupKey =
    typeof metadata?.prp_request_group_key === 'string' &&
    metadata.prp_request_group_key
      ? metadata.prp_request_group_key
      : null;

  // Check for one-off provider auto-creation (Hospital, EMS, Urgent Care)
  const autoCreatedForOneOff =
    typeof (metadata as Record<string, unknown>)?.auto_created_for_one_off ===
      'boolean' &&
    (metadata as Record<string, unknown>).auto_created_for_one_off === true;

  if (autoCreatedForOneOff) {
    const inferredType = (metadata as Record<string, unknown>)
      ?.inferred_provider_type as string | undefined;
    const PROVIDER_TYPE_LABELS: Record<string, string> = {
      HOSPITAL: 'Hospital',
      EMS: 'EMS',
      URGENT_CARE: 'Urgent Care',
    };
    const typeLabel = inferredType
      ? (PROVIDER_TYPE_LABELS[inferredType] ??
          inferredType
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()))
      : 'One-Off';
    return {
      label: typeLabel,
      color: 'amber',
      tooltip: `Auto-created for one-off ${typeLabel} provider`,
    };
  }

  if (prpPlanId) {
    return { label: 'From Provider Request Plan', color: 'blue' };
  }

  if (prpGroupKey) {
    const isHospital = isHospitalTemplateKey(
      prpGroupKey,
      request.provided_records,
    );
    return {
      label: isHospital
        ? 'Auto (hospital template)'
        : 'Auto (default template)',
      color: isHospital ? 'orange' : 'zinc',
    };
  }

  return null;
};
